import type { AutocompleteBody, AutocompleteParams } from "@apptus/esales-api";
import elevateClient from "@server/elevate";
import type { Product } from "types";
import type { Loop54AutocompleteResponse } from "types/loop54";

import type { MarketConfiguration } from "@/config/markets/const";

import mapElevateAutocompleteLink from "./mapElevateAutocompleteLink";
import mapElevateProductGroups from "./mapElevateProductGroups";
import mapCustomAttributes from "./mapCustomAttributes";

export default async function getAutocomplete(
  market: MarketConfiguration,
  input: AutocompleteParams,
  config?: AutocompleteBody,
): Promise<Loop54AutocompleteResponse & { products?: Product[] }> {
  const response = await elevateClient(market).query.autocomplete(
    {
      ...input,
      notify: process.env.NEXT_PUBLIC_ENVIRONMENT === "prod",
      presentCustom: mapCustomAttributes([], market),
      presentPrices: [
        `NG_${market.code.toLowerCase()}_best`,
        `NG_${market.code.toLowerCase()}_RRP`,
        `NG_${market.code.toLowerCase()}_pricematch`,
        `NG_${market.code.toLowerCase()}_campaign`,
      ],
      priceId: `NG_${market.code.toLowerCase()}_best`,
    },
    {
      contentLists: [
        {
          id: "designers",
          limit: 3,
          contentFilter: {
            type: ["designer"],
          },
        },
        {
          id: "brands",
          limit: 3,
          contentFilter: {
            type: ["brand"],
          },
        },
        {
          id: "categories",
          limit: 3,
          contentFilter: {
            type: ["category"],
          },
        },
      ],
      ...config,
    },
  );

  const { productSuggestions, contentLists } = response;

  const products = mapElevateProductGroups({
    productGroups: productSuggestions,
    marketConfig: market,
  });

  const brands = contentLists.find(({ id }) => id === "brands");
  const designers = contentLists.find(({ id }) => id === "designers");
  const categories = contentLists.find(({ id }) => id === "categories");

  return {
    brands: brands?.items.map(mapElevateAutocompleteLink) ?? [],
    designers: designers?.items.map(mapElevateAutocompleteLink) ?? [],
    categories: categories?.items.map(mapElevateAutocompleteLink) ?? [],
    queries: response.phraseSuggestions.map((p) => p.q),
    scopes: [],
    products,
  };
}
